import {
  ApiLineItem,
  useGetCostEstimate,
} from '@/api/services/costEstimateService';
import { FooterAppBar } from '@/components/HeaderAppBar';
import { Routes } from '@/CSIRouter';
import { useOrg } from '@/organization/useOrg';
import { useGetProject } from '@/queries/useProjects';
import { fetchSubprojects } from '@/services/subprojectService';
import { CostSummarySidebar } from '@/tools/cost-estimates/CostSummarySidebar.tsx';
import { LineItemState } from '@/tools/cost-estimates/types.ts';
import { ErrorOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Container,
  Toolbar,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CustomDivider } from '../components/CustomDivider.tsx';
import { ReadProjOverview } from './ProjectDetails/ReadProjOverview.tsx';
import { MapThumbnail } from './site-selection/MapThumbnail.tsx';

function apiItemsToLineItems(
  apiItems: ApiLineItem[],
  section: 'capital' | 'operation',
): LineItemState[] {
  return apiItems
    .filter((li) => li.section === section)
    .map((li) => ({
      clientId: crypto.randomUUID(),
      id: li.id,
      section,
      category: li.category,
      label: li.label ?? '',
      ownerAmountFiscalyear: li.ownerAmountFiscalyear ?? '',
      contractorAmountFiscalyear: li.contractorAmountFiscalyear ?? '',
      ownerAmountTotal: li.ownerAmountTotal ?? '',
      contractorAmountTotal: li.contractorAmountTotal ?? '',
      sortOrder: li.sortOrder,
      subprojectBreakdowns: (li.subprojectBreakdowns ?? []).map((bd) => ({
        subprojectId: bd.subproject,
        ownerAmountFiscalyear: bd.ownerAmountFiscalyear ?? '',
        contractorAmountFiscalyear: bd.contractorAmountFiscalyear ?? '',
        ownerAmountTotal: bd.ownerAmountTotal ?? '',
        contractorAmountTotal: bd.contractorAmountTotal ?? '',
      })),
    }));
}

export const ProjectDetails = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { selectedOrg } = useOrg();
  const [hasSubproject, setHasSubproject] = useState<boolean>(false);
  const [canDecarbFlow, setCanDecarbFlow] = useState<boolean>(false);
  const [subprojectIsLoading, setSubprojectIsLoading] = useState<boolean>(true);

  const { data: project } = useGetProject(selectedOrg?.id, projectId);

  const { data: costEstimateData, isLoading: isCostEstimateLoading } =
    useGetCostEstimate(selectedOrg?.id, projectId, { refetchInterval: 10_000 });

  const hasCostEstimate =
    !isCostEstimateLoading &&
    (!!costEstimateData?.dataSource ||
      (costEstimateData?.lineItems?.length ?? 0) > 0);

  useEffect(() => {
    if (!projectId || !selectedOrg || !project) return;
    fetchSubprojects(projectId, selectedOrg.id)
      .then((subs) => {
        if (subs.length >= 1) {
          setHasSubproject(true);
          const subprojectComplete = subs.some(
            (sub) => sub.name !== '' && sub.id !== '' && !!sub.projectShape,
          );
          if (subprojectComplete && project.name !== '') {
            setCanDecarbFlow(true);
          }
        }
        setSubprojectIsLoading(false);
      })
      .catch((e) => console.error('Failed to load subprojects:', e));
  }, [projectId, selectedOrg, project]);

  const capitalItems = useMemo(
    () => apiItemsToLineItems(costEstimateData?.lineItems ?? [], 'capital'),
    [costEstimateData],
  );
  const operationItems = useMemo(
    () => apiItemsToLineItems(costEstimateData?.lineItems ?? [], 'operation'),
    [costEstimateData],
  );

  return (
    <>
      <Container
        sx={{
          maxWidth: '97% !important',
          px: 0,
          overflow: 'auto',
          height: '98%',
        }}
      >
        <Grid
          container
          columnSpacing={{ xs: 3, sm: 4 }}
          rowSpacing={{ xs: 4, sm: 3 }}
          alignItems='stretch'
          sx={{
            // Target all direct Grid2 children to set background color at once
            '& > *': {
              backgroundColor: '#065a52 !important',
              display: 'flex',
              flexDirection: 'column',
              height: '680px',
              pb: 10,
              borderRadius: 1,
              position: 'relative',
              overflow: 'hidden',
            },
          }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ p: 5 }}>
            <ReadProjOverview />
          </Grid>
          {/* Middle panel — Subproject Details: map thumbnail + subproject list */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ p: 5, minHeight: 0 }}>
            {projectId && (
              <MapThumbnail
                projectID={projectId}
                fiscal={project?.fiscalYearDetails?.name}
              />
            )}
          </Grid>

          {/* Right panel — Cost Estimate */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ p: 5 }}>
            {/* Pane header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant='h6'
                  sx={{ color: 'white', fontWeight: 700 }}
                >
                  Cost Estimate
                </Typography>
                {project?.fiscalYearDetails?.name && (
                  <Chip
                    label={project.fiscalYearDetails.name}
                    size='small'
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
              {hasCostEstimate && (
                <Button
                  size='small'
                  variant='outlined'
                  component={Link}
                  to={'../' + Routes.Project.CostEstimates.path}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.5)',
                    textDecoration: 'none',
                  }}
                >
                  Edit
                </Button>
              )}
            </Box>

            {hasCostEstimate ? (
              <CostSummarySidebar
                readOnly
                project={project}
                capitalItems={capitalItems}
                operationItems={operationItems}
                dataSource={costEstimateData!.dataSource ?? ''}
                referenceYear={
                  costEstimateData!.referenceYear?.toString() ?? ''
                }
                rateInflationOwner={costEstimateData!.rateInflationOwner ?? ''}
                rateInflationLand={costEstimateData!.rateInflationLand ?? ''}
                rateInflationCostEscalation={
                  costEstimateData!.rateInflationCostEscalation ?? ''
                }
                localCostMultiplier={
                  costEstimateData!.localCostMultiplier ?? ''
                }
                regionalMultiplier={costEstimateData!.regionalMultiplier}
                costBasis={costEstimateData!.costBasis ?? ''}
              />
            ) : (
              !isCostEstimateLoading && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 2,
                    pt: 20,
                  }}
                >
                  <ErrorOutline
                    sx={{ fontSize: 48, color: 'rgba(255,255,255,0.5)' }}
                  />
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant='caption'
                      sx={{
                        display: 'block',
                        color: 'white',
                        fontWeight: 700,
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                      }}
                    >
                      Finalize Cost Estimate
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{
                        display: 'block',
                        color: 'rgba(255,255,255,0.6)',
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                      }}
                    >
                      {hasSubproject
                        ? 'Add cost details to continue'
                        : 'Add a subproject to continue'}
                    </Typography>
                  </Box>
                  {hasSubproject && (
                    <Button
                      size='small'
                      variant='contained'
                      component={Link}
                      to={'../' + Routes.Project.CostEstimates.path}
                      sx={{ textDecoration: 'none' }}
                    >
                      Add Cost Estimates
                    </Button>
                  )}
                </Box>
              )
            )}
          </Grid>
        </Grid>
      </Container>
      <ProjDetFooter
        hasSubproject={hasSubproject}
        subprojectIsLoading={subprojectIsLoading}
        canDecarbFlow={canDecarbFlow}
        projectId={projectId ?? ''}
      />
    </>
  );
};

const ProjDetFooter = ({
  hasSubproject,
  subprojectIsLoading,
  canDecarbFlow,
  projectId,
}: {
  hasSubproject: boolean;
  subprojectIsLoading: boolean;
  canDecarbFlow: boolean;
  projectId: string;
}) => {
  return (
    <React.Fragment>
      <FooterAppBar
        position={'sticky'}
        sx={{
          top: 'auto',
          bottom: 0,
          bgcolor: 'transparent',
          boxShadow: 'none',
          pb: { xs: 1, md: 2 },
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: { xs: 'center', md: 'center' },
            gap: { xs: 9, md: 7 },
            py: { xs: 1, md: 1 },
          }}
        >
          <Button
            variant={'contained'}
            color={'success'}
            size={'medium'}
            component={Link}
            to={'../' + Routes.Project.ProjectOverview.path}
            sx={{
              borderRadius: 1,
              py: { xs: 1.5, md: 3 },
              px: { xs: 3, md: 8 },
              width: { xs: '100%', sm: 'auto' },
              textDecoration: 'none',
            }}
          >
            PROJECT DETAILS
          </Button>
          <CustomDivider arrow sx={{ maxWidth: '12px !important' }} />
          <Button
            variant={'contained'}
            color={'success'}
            size={'medium'}
            component={Link}
            to={'../' + Routes.Project.SubprojectCreate.path}
            sx={{
              borderRadius: 1,
              py: { xs: 1.5, md: 3 },
              px: { xs: 3, md: 8 },
              width: { xs: '100%', sm: 'auto' },
              textDecoration: 'none',
            }}
          >
            CREATE SUBPROJECTS
          </Button>
          <CustomDivider arrow sx={{ maxWidth: '12px !important' }} />
          {!hasSubproject || subprojectIsLoading ? (
            <Button
              variant={'contained'}
              color={'success'}
              size={'medium'}
              disabled
              sx={{
                borderRadius: 1,
                py: { xs: 1.5, md: 3 },
                px: { xs: 3, md: 8 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {subprojectIsLoading ? 'Loading...' : 'COST ESTIMATES'}
            </Button>
          ) : (
            <Button
              variant={'contained'}
              color={'success'}
              size={'medium'}
              component={Link}
              to={'../' + Routes.Project.CostEstimates.path}
              sx={{
                borderRadius: 1,
                py: { xs: 1.5, md: 3 },
                px: { xs: 3, md: 8 },
                width: { xs: '100%', sm: 'auto' },
                textDecoration: 'none',
              }}
            >
              COST ESTIMATES
            </Button>
          )}
          {canDecarbFlow && projectId !== '' && (
            <>
              <CustomDivider arrow sx={{ maxWidth: '12px !important' }} />
              <Button
                variant={'contained'}
                color={'success'}
                size={'medium'}
                component={Link}
                to={`/project/${projectId}/decarb/overview`}
                sx={{
                  borderRadius: 1,
                  py: { xs: 1.5, md: 3 },
                  px: { xs: 3, md: 8 },
                  width: { xs: '100%', sm: 'auto' },
                  textDecoration: 'none',
                }}
              >
                Go To Decarb Hub
              </Button>
            </>
          )}
        </Toolbar>
      </FooterAppBar>
    </React.Fragment>
  );
};
